import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import PrivacyPolicy from './PrivacyPolicy';

const Banner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${props => props.theme.background};
  border-top: 1px solid ${props => props.theme.border};
  padding: 1.5rem;
  z-index: 1000;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.3);
  display: ${props => props.show ? 'block' : 'none'};
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const TextContent = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const Description = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9375rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
`;

const CookieDetails = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.8125rem;
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const AcceptButton = styled.button`
  background: ${props => props.theme.buttonPrimary};
  color: #ffffff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.buttonPrimaryHover};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const hasAccepted = localStorage.getItem('cookieConsentAccepted');
    if (!hasAccepted) {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsentAccepted', 'true');
    setShowBanner(false);
  };

  return (
    <>
      <Banner show={showBanner}>
        <BannerContent>
          <TextContent>
            <Title>Cookies & Privacy</Title>
            <Description>
              We use essential HTTP-only cookies for secure authentication and session management. Your preferences and settings are stored securely in our database.
            </Description>
            <CookieDetails>
              <strong>Server-Side Storage:</strong> Authentication uses encrypted JWT tokens in HTTP-only cookies. User preferences are stored securely server-side, not in your browser.
              We do not use tracking, advertising, or analytics cookies. Your privacy is our priority.
              <br /><br />
              <strong>Essential Cookies Only:</strong> We only use cookies that are strictly necessary for the website to function. Since these cookies are required for basic site operations, there is no option to reject them.
              <br /><br />
              <span 
                style={{ color: 'var(--color-primary)', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => setShowPrivacyModal(true)}
              >
                Learn more about our privacy practices
              </span>
            </CookieDetails>
          </TextContent>
          <ButtonContainer>
            <AcceptButton onClick={handleAccept}>
              Accept & Continue
            </AcceptButton>
          </ButtonContainer>
        </BannerContent>
      </Banner>

      <Modal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title="Privacy Policy"
      >
        <PrivacyPolicy />
      </Modal>
    </>
  );
};

export default CookieConsent;